import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import Layout from '../components/Layout';
import { usePermissions } from '../hooks/usePermissions';
import { fetchUserById, fetchUsers } from '../api/User';
import { AuthContext } from '../context/AuthContext';
import {
  assignTelecallingCalls,
  fetchTelecallingQueue,
  fetchTelecallingSummary,
  logTelecallingInteraction,
  uploadTelecallingLeads,
} from '../api/Telecalling';
import { User } from '../types/User';
import {
  TelecallingCounts,
  TelecallingInterestLevel,
  TelecallingOutcome,
  TelecallingQueueItem,
  TelecallingSummaryByUser,
} from '../types/Telecalling';
import { filterEnabledRegularUsers } from '../utils/userFilters';
import '../css/Pages.css';
import '../css/Telecalling.css';

const outcomeOptions: Array<{ value: TelecallingOutcome; label: string }> = [
  { value: 'invalid_number', label: 'Not a valid number' },
  { value: 'not_picked', label: 'Call not picked' },
  { value: 'not_interested', label: 'Not interested' },
  { value: 'interested', label: 'Interested (set level)' },
  { value: 'converted', label: 'Converted lead' },
];

const TelecallingPage: React.FC = () => {
  const permissions = usePermissions();
  const isAdmin = permissions.canManageTelecalling;
  const authContext = useContext(AuthContext) as {
    user?: { id?: number | string; phoneNumber?: string; phone?: string } | null;
  };
  const [agentPhoneNumber, setAgentPhoneNumber] = useState(
    authContext.user?.phoneNumber || authContext.user?.phone || '',
  );

  const [users, setUsers] = useState<User[]>([]);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [queueLoading, setQueueLoading] = useState(false);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [queueLimit, setQueueLimit] = useState(10);
  const [queue, setQueue] = useState<TelecallingQueueItem[]>([]);
  const [queueTotal, setQueueTotal] = useState<number | null>(null);
  const [summaryByUser, setSummaryByUser] = useState<TelecallingSummaryByUser[]>([]);
  const [summaryTotals, setSummaryTotals] = useState<TelecallingCounts | null>(null);
  const [assignmentTargets, setAssignmentTargets] = useState<Record<number, number>>({});
  const [revealedPhoneNumbers, setRevealedPhoneNumbers] = useState<Set<string>>(new Set());

  useEffect(() => {
    const phoneFromToken = authContext.user?.phoneNumber || authContext.user?.phone || '';
    setAgentPhoneNumber(phoneFromToken);

    if (phoneFromToken || !authContext.user?.id) return;

    fetchUserById(String(authContext.user.id))
      .then((user) => setAgentPhoneNumber(user.phoneNumber || ''))
      .catch(() => setAgentPhoneNumber(''));
  }, [authContext.user]);

  const loadUsers = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const data = await fetchUsers();
      setUsers(filterEnabledRegularUsers(Array.isArray(data) ? data : []));
    } catch (error: any) {
      toast.error(error?.message || 'Failed to load users');
    }
  }, [isAdmin]);

  const loadSummary = useCallback(async () => {
    setSummaryLoading(true);
    try {
      const today = new Date();
      const date = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
      const data = await fetchTelecallingSummary({ date });
      setSummaryByUser(Array.isArray(data.byUser) ? data.byUser : []);
      setSummaryTotals(data.totals || null);
    } catch (error: any) {
      setSummaryByUser([]);
      setSummaryTotals(null);
    } finally {
      setSummaryLoading(false);
    }
  }, []);

  const loadQueue = useCallback(async () => {
    setQueueLoading(true);
    try {
      const data = await fetchTelecallingQueue(queueLimit);
      setQueue(data.items || []);
      setQueueTotal(typeof data.total === 'number' ? data.total : null);
    } catch (error: any) {
      setQueue([]);
      toast.error(error?.message || 'Failed to load telecalling queue');
    } finally {
      setQueueLoading(false);
    }
  }, [queueLimit]);

  useEffect(() => {
    loadUsers();
    loadSummary();
  }, [loadUsers, loadSummary]);

  useEffect(() => {
    loadQueue();
  }, [loadQueue]);

  const handleUpload = async () => {
    if (!uploadFile) {
      toast.error('Select a CSV or Excel file to upload');
      return;
    }
    setUploading(true);
    try {
      await uploadTelecallingLeads(uploadFile);
      toast.success('Leads uploaded');
      setUploadFile(null);
      loadSummary();
      loadQueue();
    } catch (error: any) {
      toast.error(error?.message || 'Failed to upload leads');
    } finally {
      setUploading(false);
    }
  };

  const handleAssign = async () => {
    const payload = Object.entries(assignmentTargets)
      .map(([userId, callCount]) => ({ userId: Number(userId), callCount: Number(callCount || 0) }))
      .filter(item => item.userId && item.callCount > 0);
    if (payload.length === 0) {
      toast.error('Enter call targets before assigning');
      return;
    }
    setAssigning(true);
    try {
      await assignTelecallingCalls(payload);
      toast.success('Call targets assigned');
      loadSummary();
      loadQueue();
    } catch (error: any) {
      toast.error(error?.message || 'Failed to assign calls');
    } finally {
      setAssigning(false);
    }
  };

  const summaryMap = useMemo(() => {
    const map = new Map<number, TelecallingSummaryByUser>();
    summaryByUser.forEach(item => map.set(item.userId, item));
    return map;
  }, [summaryByUser]);

  const enabledUserIds = useMemo(() => {
    return new Set(users.map(u => u.id).filter((id): id is number => typeof id === 'number'));
  }, [users]);

  const visibleSummaryByUser = useMemo(() => {
    return summaryByUser.filter(row => enabledUserIds.has(row.userId));
  }, [summaryByUser, enabledUserIds]);

  const hasSummary = Boolean(summaryTotals) || visibleSummaryByUser.length > 0;

  return (
    <Layout>
      <div className="page-container telecalling-page">
        <div className="page-header telecalling-header">
          <div>
            <h2>Telecalling</h2>
            <div className="telecalling-subtitle">
              Track call assignments, log outcomes, and keep leads updated.
            </div>
          </div>
        </div>

        {isAdmin && (
          <div className="telecalling-grid">
            <div className="card telecalling-card">
              <h3>Upload Leads</h3>
              <p>Upload a CSV or Excel file with names and phone numbers.</p>
              <input
                type="file"
                accept=".csv,.xls,.xlsx"
                onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
              />
              <button className="btn" onClick={handleUpload} disabled={uploading}>
                {uploading ? 'Uploading…' : 'Upload'}
              </button>
            </div>

            <div className="card telecalling-card">
              <h3>Assign Call Targets</h3>
              <div className="telecalling-table">
                {users.length === 0 && <div className="telecalling-empty">No users found.</div>}
                {users.map((user) => {
                  const summary = summaryMap.get(user.id as number);
                  const assigned = summary?.assigned || 0;
                  const completed = summary?.completed || 0;
                  return (
                    <div key={user.id} className="telecalling-row">
                      <div className="telecalling-user">
                        <div className="telecalling-user-name">{user.name}</div>
                        <div className="telecalling-user-meta">{user.email}</div>
                      </div>
                      <div className="telecalling-user-stats">
                        <span>{completed}/{assigned} calls</span>
                      </div>
                      <input
                        type="number"
                        min="0"
                        step="1"
                        placeholder="Assign calls"
                        value={assignmentTargets[user.id as number] ?? ''}
                        onChange={(e) =>
                          setAssignmentTargets(prev => ({
                            ...prev,
                            [user.id as number]: Number(e.target.value || 0),
                          }))
                        }
                      />
                    </div>
                  );
                })}
              </div>
              <button className="btn" onClick={handleAssign} disabled={assigning}>
                {assigning ? 'Assigning…' : 'Assign Targets'}
              </button>
            </div>

            <div className="card telecalling-card telecalling-card--summary">
              <h3>Team Call Summary</h3>
              {summaryLoading && <div>Loading…</div>}
              {!summaryLoading && !hasSummary && (
                <div className="telecalling-empty">No call activity yet.</div>
              )}
              {!summaryLoading && hasSummary && (
                <div className="telecalling-summary-table">
                  <div className="telecalling-summary-header">
                    <span>Employee</span>
                    <span>Calls</span>
                    <span>Assigned</span>
                    <span>Completed</span>
                    <span>Invalid</span>
                    <span>Not Picked</span>
                    <span>Not Interested</span>
                    <span>Interested</span>
                    <span>Converted</span>
                  </div>
                  {summaryTotals && (
                    <div className="telecalling-summary-row telecalling-summary-row--totals">
                      <span>Totals</span>
                      <span>{summaryTotals.completed}/{summaryTotals.assigned}</span>
                      <span>{summaryTotals.assigned}</span>
                      <span>{summaryTotals.completed}</span>
                      <span>{summaryTotals.invalidNumber}</span>
                      <span>{summaryTotals.notPicked}</span>
                      <span>{summaryTotals.notInterested}</span>
                      <span>{summaryTotals.interested}</span>
                      <span>{summaryTotals.converted}</span>
                    </div>
                  )}
                  {visibleSummaryByUser.map((row) => {
                    const user = users.find(u => u.id === row.userId);
                    return (
                      <div key={row.userId} className="telecalling-summary-row">
                        <span>{user?.name || `User #${row.userId}`}</span>
                        <span>{row.completed}/{row.assigned}</span>
                        <span>{row.assigned}</span>
                        <span>{row.completed}</span>
                        <span>{row.invalidNumber}</span>
                        <span>{row.notPicked}</span>
                        <span>{row.notInterested}</span>
                        <span>{row.interested}</span>
                        <span>{row.converted}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        <div className="card telecalling-card telecalling-queue-card">
          <div className="telecalling-queue-header">
            <div className="telecalling-queue-title-section">
              <h3 className="telecalling-queue-title">My Call Queue</h3>
              <div className="telecalling-queue-subtitle">
                {queueTotal !== null ? (
                  <>
                    Showing <strong>{queue.length}</strong> of <strong>{queueTotal}</strong> assigned leads
                  </>
                ) : (
                  <>
                    <strong>{queue.length}</strong> {queue.length === 1 ? 'lead' : 'leads'}
                  </>
                )}
              </div>
            </div>
            <div className="telecalling-queue-controls">
              <div className="telecalling-pagination-group">
                <label className="telecalling-pagination-label">
                  Show next
                  <input
                    type="number"
                    min="1"
                    max="100"
                    value={queueLimit}
                    onChange={(e) => setQueueLimit(Number(e.target.value || 10))}
                    className="telecalling-pagination-input"
                  />
                  leads
                </label>
                <button 
                  className="btn btn-secondary telecalling-load-btn" 
                  onClick={loadQueue} 
                  disabled={queueLoading}
                >
                  {queueLoading ? (
                    <>
                      <svg className="telecalling-spinner" width="16" height="16" viewBox="0 0 24 24" fill="none">
                        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" opacity="0.25" />
                        <path fill="currentColor" d="M4 12a8 8 0 0 1 8-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 0 1 4 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" opacity="0.75" />
                      </svg>
                      Loading…
                    </>
                  ) : (
                    <>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="23 4 23 10 17 10" />
                        <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
                      </svg>
                      Load
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          {queueLoading && (
            <div className="telecalling-loading-state">
              <svg className="telecalling-spinner" width="24" height="24" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" opacity="0.25" />
                <path fill="currentColor" d="M4 12a8 8 0 0 1 8-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 0 1 4 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" opacity="0.75" />
              </svg>
              <span>Loading leads...</span>
            </div>
          )}

          {queue.length === 0 && !queueLoading && (
            <div className="telecalling-empty-state">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" opacity="0.3">
                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
              </svg>
              <p className="telecalling-empty-title">No leads assigned yet</p>
              <p className="telecalling-empty-description">Leads will appear here once they are assigned to you.</p>
            </div>
          )}

          {queue.length > 0 && !queueLoading && (
            <div className="telecalling-queue">
              {queue.map((item) => {
                const leadId = String(item.lead.id);
                return (
                <TelecallingLeadCard 
                  key={item.lead.id} 
                  item={item} 
                  onSaved={loadQueue}
                  agentPhoneNumber={agentPhoneNumber}
                  isRevealed={revealedPhoneNumbers.has(leadId)}
                  onReveal={() => {
                    setRevealedPhoneNumbers(prev => {
                      const next = new Set(prev);
                      next.add(leadId);
                      return next;
                    });
                  }}
                />
                );
              })}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

const TelecallingLeadCard: React.FC<{ 
  item: TelecallingQueueItem; 
  onSaved: () => void;
  agentPhoneNumber: string;
  isRevealed: boolean;
  onReveal: () => void;
}> = ({
  item,
  onSaved,
  agentPhoneNumber,
  isRevealed,
  onReveal,
}) => {
  const whatsappMessage = `Hi \u{1F44B}
Thank you for taking the time to speak with us today. It was lovely interacting with you! \u{1F60A}

If you need any assistance with flights, hotels, visas or holiday packages, we\u2019re just a ping away \u2014 happy to help anytime!

${agentPhoneNumber ? `\u{1F4DE} Call Us: ${agentPhoneNumber}\n` : ''}
\u{1F310} Website: https://otaprime.com

Looking forward to planning your next trip \u{2708}\u{FE0F}\u2728
Team OTA Prime Holidays`;
  const lead = item.lead;
  const phone = lead.phone || (lead as any).phoneNumber || (lead as any).phone_number || '';
  const leadName = lead.name || (lead as any).fullName || (lead as any).customerName || 'Unnamed lead';
  const [outcome, setOutcome] = useState<TelecallingOutcome | ''>('');
  const [interestLevel, setInterestLevel] = useState<TelecallingInterestLevel | ''>('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const lastInteraction = item.lastInteraction;
  const hasInteraction = !!lastInteraction?.outcome;
  const getOutcomeLabel = (outcome: string) => {
    return outcome.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const maskPhoneNumber = (phoneNum: string): string => {
    if (!phoneNum || phoneNum.length < 4) return phoneNum;
    // Show last 4 digits, mask the rest with •
    const visibleDigits = phoneNum.slice(-4);
    const maskedLength = phoneNum.length - 4;
    return '•'.repeat(maskedLength) + visibleDigits;
  };

  const displayPhone = phone && !isRevealed ? maskPhoneNumber(phone) : phone;

  const formatWhatsAppNumber = (raw: string) => {
    const digits = (raw || '').replace(/\D/g, '');
    if (!digits) return '';
    if (digits.length < 10) return '';
    const lastTen = digits.slice(-10);
    return `91${lastTen}`;
  };

  const whatsappNumber = formatWhatsAppNumber(phone);
  const whatsappUrl = whatsappNumber
    ? `https://api.whatsapp.com/send?phone=${whatsappNumber}&text=${encodeURIComponent(whatsappMessage)}`
    : '#';

  const handlePhoneClick = (e: React.MouseEvent) => {
    if (!isRevealed && phone) {
      e.preventDefault();
      onReveal();
    }
  };

  const handleSave = async () => {
    if (!outcome) {
      toast.error('Select an outcome');
      return;
    }
    if (outcome === 'interested' && !interestLevel) {
      toast.error('Select interest level');
      return;
    }
    setSaving(true);
    try {
      await logTelecallingInteraction(lead.id, {
        outcome: outcome as TelecallingOutcome,
        interestLevel: outcome === 'interested' ? Number(interestLevel) : undefined,
        notes: notes || undefined,
      });
      toast.success('Call logged');
      setOutcome('');
      setInterestLevel('');
      setNotes('');
      onSaved();
    } catch (error: any) {
      toast.error(error?.message || 'Failed to log call');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={`telecalling-lead-card ${hasInteraction ? 'telecalling-lead-card--has-interaction' : ''}`}>
      <div className="telecalling-lead-header">
        <div className="telecalling-lead-info">
          <div className="telecalling-lead-name-row">
            <h4 className="telecalling-lead-name">{leadName}</h4>
            {hasInteraction && lastInteraction && (
              <span className="telecalling-lead-badge">
                {getOutcomeLabel(lastInteraction.outcome)}
                {lastInteraction.interestLevel ? ` L${lastInteraction.interestLevel}` : ''}
              </span>
            )}
          </div>
          {phone ? (
            <a 
              href={isRevealed ? `tel:${phone}` : '#'} 
              className={`telecalling-lead-phone-link ${!isRevealed ? 'telecalling-lead-phone-link--masked' : ''}`}
              onClick={handlePhoneClick}
              title={!isRevealed ? 'Click to reveal phone number' : ''}
            >
              <svg className="telecalling-phone-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
              </svg>
              {displayPhone}
              {!isRevealed && (
                <span className="telecalling-reveal-hint"> (click to reveal)</span>
              )}
            </a>
          ) : (
            <span className="telecalling-lead-phone-empty">No phone number</span>
          )}
        </div>
        <div className="telecalling-contact-actions">
          {phone ? (
            <a className="btn btn-primary telecalling-call-btn" href={`tel:${phone}`}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
              </svg>
              Call
            </a>
          ) : (
            <button className="btn btn-primary telecalling-call-btn" disabled>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
              </svg>
              Call
            </button>
          )}
          {whatsappNumber ? (
            <a
              className="btn telecalling-whatsapp-btn"
              href={whatsappUrl}
              target="_blank"
              rel="noreferrer"
            >
              <svg width="16" height="16" viewBox="0 0 32 32" fill="currentColor" aria-hidden="true">
                <path d="M19.11 17.13c-.28-.14-1.64-.81-1.9-.9-.26-.09-.45-.14-.64.14-.19.28-.73.9-.9 1.09-.16.19-.33.21-.61.07-.28-.14-1.18-.43-2.25-1.38-.83-.74-1.39-1.66-1.55-1.94-.16-.28-.02-.43.12-.57.12-.12.28-.33.42-.5.14-.17.19-.28.28-.47.09-.19.05-.35-.02-.5-.07-.14-.64-1.54-.88-2.1-.23-.55-.47-.47-.64-.48-.16-.01-.35-.01-.54-.01-.19 0-.5.07-.76.35-.26.28-1 1-1 2.44 0 1.43 1.03 2.81 1.17 3 .14.19 2.03 3.1 4.92 4.35.69.3 1.23.48 1.65.62.69.22 1.32.19 1.82.12.56-.08 1.64-.67 1.87-1.32.23-.65.23-1.2.16-1.32-.07-.12-.26-.19-.54-.33z" />
                <path d="M16.07 3C9.41 3 4 8.39 4 15.04c0 2.36.69 4.56 1.88 6.43L4 29l7.71-1.83c1.78.97 3.82 1.52 6 1.52 6.66 0 12.07-5.39 12.07-12.04C29.78 8.39 22.73 3 16.07 3zm0 21.89c-2.02 0-3.9-.59-5.48-1.61l-.39-.25-4.58 1.09 1.07-4.47-.25-.41a9.82 9.82 0 0 1-1.55-5.2c0-5.42 4.44-9.83 9.98-9.83 5.5 0 9.98 4.41 9.98 9.83 0 5.42-4.48 9.85-9.78 9.85z" />
              </svg>
              WhatsApp
            </a>
          ) : (
            <button className="btn telecalling-whatsapp-btn" disabled>
              <svg width="16" height="16" viewBox="0 0 32 32" fill="currentColor" aria-hidden="true">
                <path d="M19.11 17.13c-.28-.14-1.64-.81-1.9-.9-.26-.09-.45-.14-.64.14-.19.28-.73.9-.9 1.09-.16.19-.33.21-.61.07-.28-.14-1.18-.43-2.25-1.38-.83-.74-1.39-1.66-1.55-1.94-.16-.28-.02-.43.12-.57.12-.12.28-.33.42-.5.14-.17.19-.28.28-.47.09-.19.05-.35-.02-.5-.07-.14-.64-1.54-.88-2.1-.23-.55-.47-.47-.64-.48-.16-.01-.35-.01-.54-.01-.19 0-.5.07-.76.35-.26.28-1 1-1 2.44 0 1.43 1.03 2.81 1.17 3 .14.19 2.03 3.1 4.92 4.35.69.3 1.23.48 1.65.62.69.22 1.32.19 1.82.12.56-.08 1.64-.67 1.87-1.32.23-.65.23-1.2.16-1.32-.07-.12-.26-.19-.54-.33z" />
                <path d="M16.07 3C9.41 3 4 8.39 4 15.04c0 2.36.69 4.56 1.88 6.43L4 29l7.71-1.83c1.78.97 3.82 1.52 6 1.52 6.66 0 12.07-5.39 12.07-12.04C29.78 8.39 22.73 3 16.07 3zm0 21.89c-2.02 0-3.9-.59-5.48-1.61l-.39-.25-4.58 1.09 1.07-4.47-.25-.41a9.82 9.82 0 0 1-1.55-5.2c0-5.42 4.44-9.83 9.98-9.83 5.5 0 9.98 4.41 9.98 9.83 0 5.42-4.48 9.85-9.78 9.85z" />
              </svg>
              WhatsApp
            </button>
          )}
        </div>
      </div>

      <div className="telecalling-lead-form">
        <div className="telecalling-form-row">
          <label className="telecalling-form-field">
            <span className="telecalling-form-label">Outcome</span>
            <select 
              value={outcome} 
              onChange={(e) => setOutcome(e.target.value as TelecallingOutcome)}
              className="telecalling-select"
            >
              <option value="">Select outcome</option>
              {outcomeOptions.map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </label>

          {outcome === 'interested' && (
            <label className="telecalling-form-field">
              <span className="telecalling-form-label">Interest Level</span>
              <select 
                value={interestLevel} 
                onChange={(e) => setInterestLevel(Number(e.target.value) as TelecallingInterestLevel)}
                className="telecalling-select"
              >
                <option value="">Select level</option>
                {[1, 2, 3, 4, 5].map(level => (
                  <option key={level} value={level}>Level {level}</option>
                ))}
              </select>
            </label>
          )}
        </div>

        <label className="telecalling-form-field telecalling-notes">
          <span className="telecalling-form-label">Notes</span>
          <textarea 
            value={notes} 
            onChange={(e) => setNotes(e.target.value)} 
            placeholder="Capture notes (optional)"
            className="telecalling-textarea"
          />
        </label>
      </div>

      <div className="telecalling-lead-actions">
        <button 
          className="btn btn-primary telecalling-save-btn" 
          onClick={handleSave} 
          disabled={saving || !outcome}
        >
          {saving ? (
            <>
              <svg className="telecalling-spinner" width="16" height="16" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" opacity="0.25" />
                <path fill="currentColor" d="M4 12a8 8 0 0 1 8-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 0 1 4 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" opacity="0.75" />
              </svg>
              Saving…
            </>
          ) : (
            <>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
                <polyline points="17 21 17 13 7 13 7 21" />
                <polyline points="7 3 7 8 15 8" />
              </svg>
              Save Outcome
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default TelecallingPage;
